import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUpRight } from "lucide-react";

function HeroContent({ ghost = false }) {
  const tid = (id) => (ghost ? {} : { "data-testid": id });
  return (
    <div className="relative max-w-5xl mx-auto px-4 md:px-8 min-h-[86vh] flex flex-col items-center justify-center text-center py-24 md:py-28">
      {/* Eyebrow — name */}
      <p
        className="font-display text-base md:text-lg tracking-tighter text-[#3F3F46] mb-5"
        {...tid("hero-name")}
      >
        Parastou Saba<span className="text-[#4C1D95]">.</span>
      </p>

      {/* Headline */}
      <h1
        className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tighter max-w-4xl"
        {...tid("hero-headline")}
      >
        Turn product complexity into{" "}
        <span className="text-[#4C1D95] underline decoration-4 decoration-[#4C1D95] underline-offset-[0.12em]">
          UX clarity
        </span>
        <span className="text-[#0A0A0A]">.</span>
      </h1>

      {/* SEO subheading */}
      <h2
        className="font-display text-lg md:text-xl tracking-tight text-[#0A0A0A] max-w-2xl mx-auto mt-5 md:mt-6"
        {...tid("hero-subheading")}
      >
        UX designer &amp; consultant in Toronto for complex SaaS
        and high-trust products.
      </h2>

      {/* Subline */}
      <p
        className="text-base md:text-lg leading-relaxed text-[#595959] max-w-2xl mx-auto mt-4 md:mt-5"
        {...tid("hero-subtext")}
      >
        UX &amp; interaction design for SaaS and product teams drowning in
        tangled flows, dense interfaces, and operational complexity.
      </p>

      {/* CTAs */}
      <div
        className="flex flex-wrap items-center justify-center gap-4 mt-9 md:mt-10"
        {...tid("hero-ctas")}
      >
        <a
          href="#work"
          className="btn-hero group inline-flex items-center bg-[#4C1D95] text-[#FFFFFF] px-7 py-4 label-mono border border-[#4C1D95]"
          tabIndex={ghost ? -1 : undefined}
          {...tid("hero-cta-work")}
        >
          <span className="inline-flex items-center gap-3">
            View Work
            <ArrowDown
              size={16}
              strokeWidth={1.5}
              className="transition-transform group-hover:translate-y-0.5"
            />
          </span>
        </a>
        <Link
          to="/services"
          className="btn-hero btn-hero--ghost group inline-flex items-center border border-[#0A0A0A] text-[#0A0A0A] px-7 py-4 label-mono"
          tabIndex={ghost ? -1 : undefined}
          {...tid("hero-cta-audit")}
        >
          <span className="inline-flex items-center gap-3">
            View services
            <ArrowUpRight
              size={16}
              strokeWidth={1.5}
              className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </span>
        </Link>
      </div>

      {/* Stats strip */}
      <div
        className="mt-12 md:mt-14 w-full max-w-3xl grid grid-cols-2 md:grid-cols-3 gap-y-8 gap-x-6"
        {...tid("hero-stats")}
      >
        {[
          { n: "7+", l: "Years designing complex products" },
          { n: "100+", l: "Products worked on" },
          { n: "24h", l: "Typical reply time" },
        ].map((s) => (
          <div key={s.l} className="text-center px-1">
            <div className="font-display text-3xl md:text-4xl tracking-tighter text-[#0A0A0A]">
              {s.n}
            </div>
            <div className="label-mono text-[#595959] leading-snug mt-3 max-w-[18ch] mx-auto">
              {s.l}
            </div>
          </div>
        ))}
      </div>

      {/* Credibility strip */}
      <div
        className="mt-12 md:mt-14 pt-6 border-t border-[#0A0A0A]/15 w-full max-w-2xl"
        {...tid("hero-credibility")}
      >
        <p className="label-mono text-[#8A8A8A] tracking-[0.12em]">
          Enterprise instrument software · Fintech · SaaS platforms
        </p>
      </div>
    </div>
  );
}

export default function Hero() {
  const heroRef = useRef(null);

  // A soft circular magnifier that eases toward the cursor and enlarges the
  // content beneath it. On touch/no-cursor devices it drifts on its own.
  // Fully disabled for prefers-reduced-motion.
  useEffect(() => {
    const sec = heroRef.current;
    if (!sec) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const finePointer = window.matchMedia(
      "(hover: hover) and (pointer: fine)"
    ).matches;

    const r0 = sec.getBoundingClientRect();
    let cx = r0.width / 2;
    let cy = r0.height * 0.42;
    let tx = cx;
    let ty = cy;
    let raf = 0;
    const t0 = performance.now();

    const setVars = () => {
      sec.style.setProperty("--lx", `${cx.toFixed(1)}px`);
      sec.style.setProperty("--ly", `${cy.toFixed(1)}px`);
    };
    setVars();
    sec.classList.add("lens-active");

    const loop = (now) => {
      if (!finePointer) {
        const r = sec.getBoundingClientRect();
        const t = (now - t0) / 1000;
        tx = r.width * (0.5 + 0.3 * Math.sin(t * 0.34));
        ty = r.height * (0.46 + 0.26 * Math.sin(t * 0.26 + 1.2));
      }
      cx += (tx - cx) * 0.08;
      cy += (ty - cy) * 0.08;
      setVars();
      raf = requestAnimationFrame(loop);
    };

    const onMove = (e) => {
      const r = sec.getBoundingClientRect();
      tx = e.clientX - r.left;
      ty = e.clientY - r.top;
    };

    if (finePointer) sec.addEventListener("mousemove", onMove);
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      sec.removeEventListener("mousemove", onMove);
    };
  }, []);

  return (
    <section
      id="top"
      ref={heroRef}
      className="hero-lens relative border-b border-[#0A0A0A] overflow-hidden bg-[#FAF9F6]"
      data-testid="hero-section"
    >
      {/* Base content */}
      <div className="relative z-10">
        <HeroContent />
      </div>

      {/* Magnifier layer — a scaled copy, clipped to the lens circle */}
      <div className="hero-lens-layer" aria-hidden="true">
        <div className="hero-lens-scale">
          <HeroContent ghost />
        </div>
      </div>

      {/* Lens boundary */}
      <div className="hero-lens-ring" aria-hidden="true" />
    </section>
  );
}
